import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import {
  useConnectorBindingConfiguration,
  useParameterAddition,
  useFormValidation,
} from '../../../shared/lib';
import { useUIStore } from '../../../shared/stores/useUIStore';
import { usePcfValidationStore } from '../../../shared/stores/usePcfValidationStore';
import { useActiveProjectKey } from '../../../shared/hooks/ActiveDocumentProvider';
import { CONNECTOR_BINDING_FIELDS } from '../../../shared/config/validationConfig';
import MainLayout from '../../../shared/ui/layout/MainLayout';
import { layoutGap } from '../../../theme/theme';
import { OperationProgressWindowRequest } from '../../../shared/ui/progress';
import CompactFieldRow from '../../../shared/ui/workflow/CompactFieldRow';
import WorkflowFooter from '../../../shared/ui/workflow/WorkflowFooter';
import {
  ConnectorFieldKey,
  FIELD_SECTION_LOOKUP,
  SECTION_DEFINITIONS,
  describeConnectorTargetApplication,
  getSectionFromFocusState,
} from '../lib/connectorBindingSections';
import { useConnectorParameterOptionGroups } from '../lib/useConnectorParameterOptionGroups';
import { useParameterDropdownOptions } from '../lib/useParameterDropdownOptions';
import { useSetupRevisionStep } from '../lib/useSetupRevisionStep';
import { filterTextParameterOptions } from '../../../shared/lib/revit/parameterOptionTypeFilter';
import { describeUndoPlanAffordance } from '../../../shared/lib/parameterUndo/undoAffordance';
import HelpTooltipTrigger from '../../../shared/ui/common/HelpTooltipTrigger';
import {
  ActiveSectionHeader,
  EditorSurface,
  MappingFieldGrid,
  type SectionSummaryItem,
  SectionSwitcher,
  WorkflowNotice,
  WorkflowStepShell,
} from '../../../shared/ui/workflow/WorkflowStepComponents';
import ParameterSetupScopePanel, {
  formatParameterSetupTargetLabel,
} from '../../../shared/ui/workflow/ParameterSetupScopePanel';
import {
  resolveConnectorDestinationFocus,
  ValidationNavigationNotice,
} from '../../pcf-validation';
import { connectorBindingTooltips } from '../../../shared/config/wizardTooltips';
import { getContinueToNextWorkflowStepText } from '../../../shared/config/workflowUi';
import {
  asStubbedConnectorOutcome,
  buildE2eConnectorParameterResult,
  getE2eBootstrapConfig,
  type E2eStubbedConnectorOutcome,
} from '../../../shared/config/e2eBootstrap';
import {
  recordConnectorLiveEvidenceFrontendResult,
  resetConnectorLiveEvidence,
} from '../../../shared/config/connectorLiveEvidence';
import {
  buildConnectorParameterFailure,
  changedProjectIdentityFailure,
  describeConnectorTargetFailure,
  missingProjectIdentityFailure,
  type ConnectorParameterFailure,
} from '../lib/connectorTargetFailure';
import RevitParameterCatalogAction from './components/RevitParameterCatalogAction';

interface ConnectorBindingPageProps {
  onGoBack: () => void;
  onContinue: () => void;
}

/** Live E2E drives the outcome directly, so no Revit mutation is attempted. */
function reportStubbedConnectorOutcome(
  outcome: E2eStubbedConnectorOutcome
): ConnectorParameterFailure | null {
  const result = buildE2eConnectorParameterResult(outcome);
  recordConnectorLiveEvidenceFrontendResult(result);

  return result.success ? null : buildConnectorParameterFailure(result);
}

const ConnectorBindingPage: React.FC<ConnectorBindingPageProps> = ({
  onGoBack,
  onContinue,
}) => {
  const { config, updateField, setParameterTarget } = useConnectorBindingConfiguration();
  const selectedConnectorTargetRef = useRef({
    destination: config.parameterDestination, bindingKind: config.parameterBindingKind,
  });
  selectedConnectorTargetRef.current = { destination: config.parameterDestination, bindingKind: config.parameterBindingKind };
  const defaultPropertiesConfirmed = useUIStore((state) => state.defaultPropertiesConfirmed);
  const setDefaultPropertiesConfirmed = useUIStore((state) => state.setDefaultPropertiesConfirmed);
  const selectedOption = useUIStore((state) => state.selectedOption);
  const activeProjectKey = useActiveProjectKey();
  const [parameterFailure, setParameterFailure] = useState<ConnectorParameterFailure | null>(null);
  // Only page-answerable outcomes short-circuit here. `recovery-required`
  // narrows to null on purpose so it falls through to the real transaction,
  // which is the only thing that can undo a checkpoint and then fail the apply.
  const e2eConnectorParameterOutcome = useMemo(
    () => typeof window === 'undefined'
      ? null
      : asStubbedConnectorOutcome(
        getE2eBootstrapConfig(window.location.search, process.env.NODE_ENV)
          ?.connectorParameterOutcome ?? null
      ),
    []
  );
  const activeSection = useUIStore((state) => state.sections.connectorBinding);
  const setActiveSection = useUIStore((state) => state.setConnectorBindingSection);
  const issueNavigationContext = usePcfValidationStore((state) => state.issueNavigationContext);
  const { options: dropdownParameterOptions, isLoadingAvailableOptions, loadAvailableOptions } =
    useParameterDropdownOptions(activeProjectKey, { loadOnMount: false });
  const textParameterOptions = useMemo(
    () => filterTextParameterOptions(dropdownParameterOptions),
    [dropdownParameterOptions]
  );

  const { optionGroups } = useConnectorParameterOptionGroups(textParameterOptions);

  const {
    isAdding: isAddingParameters,
    parameterProgress,
    activeConnectorOperation,
    connectorSelectionBlockedByMismatch,
    applyConnectorTarget,
    undoLastConnectorParameterAddition,
    lastConnectorUndoPlan,
  } = useParameterAddition();
  const connectorOperationLive = activeConnectorOperation != null;

  const { validation, getFieldError, markFieldTouched, setShowValidationErrors, canProceed } =
    useFormValidation({
      config,
      fields: CONNECTOR_BINDING_FIELDS,
    });

  const validationFocusFields = useMemo(
    () =>
      new Set(
        (issueNavigationContext?.actionParams?.connectorFields || issueNavigationContext?.affectedItems || [])
          .map((item) => item.trim().toUpperCase().replace(/[\s_]+/g, '-'))
          .filter(Boolean)
      ),
    [issueNavigationContext?.actionParams?.connectorFields, issueNavigationContext?.affectedItems]
  );

  const isValidationFocused = useCallback(
    (...tokens: string[]) =>
      tokens.some((token) => validationFocusFields.has(token.trim().toUpperCase().replace(/[\s_]+/g, '-'))),
    [validationFocusFields]
  );

  const {
    beginReconciliation: beginSetupReconciliation,
    blockedReason: revisionBlockedReason,
    markValueChanged: markSetupValueChanged,
    recovery: setupRevisionRecovery,
    retryRecovery: retrySetupRevision,
  } = useSetupRevisionStep();

  const createFieldHandler = useCallback(
    (fieldName: ConnectorFieldKey) => (value: string) => {
      updateField(fieldName, value);
      markFieldTouched(fieldName);
      markSetupValueChanged();
    },
    [markFieldTouched, markSetupValueChanged, updateField]
  );

  const fieldHandlers = useMemo<Record<ConnectorFieldKey, (value: string) => void>>(
    () => ({
      endpoint1Value: createFieldHandler('endpoint1Value'),
      endpoint2Value: createFieldHandler('endpoint2Value'),
      endpoint3Value: createFieldHandler('endpoint3Value'),
      endpoint4Value: createFieldHandler('endpoint4Value'),
      materialValue: createFieldHandler('materialValue'),
      designStdValue: createFieldHandler('designStdValue'),
      pressureClassValue: createFieldHandler('pressureClassValue'),
      insulationSpecValue: createFieldHandler('insulationSpecValue'),
      insulationThicknessValue: createFieldHandler('insulationThicknessValue'),
      tracingSpecValue: createFieldHandler('tracingSpecValue'),
      itemDescriptionValue: createFieldHandler('itemDescriptionValue'),
    }),
    [createFieldHandler]
  );

  const selectedTargetLabel = formatParameterSetupTargetLabel(
    config.parameterDestination,
    config.parameterBindingKind
  );
  const connectorTargetApplication = describeConnectorTargetApplication(
    config.parameterDestination,
    config.parameterBindingKind
  );
  const setupConfirmed = defaultPropertiesConfirmed;
  const connectorUndoAffordance = describeUndoPlanAffordance(lastConnectorUndoPlan);
  const hasUndoableConnectorAddition =
    Boolean(activeProjectKey) &&
    lastConnectorUndoPlan?.projectKey === activeProjectKey &&
    connectorUndoAffordance.visible;
  const continueToNextStepText = useMemo(
    () => getContinueToNextWorkflowStepText('connectorBinding', selectedOption),
    [selectedOption]
  );
  const canProceedWithForm = canProceed(
    setupConfirmed && !isAddingParameters && !connectorOperationLive && !revisionBlockedReason
  );

  const sectionStates = useMemo(
    () =>
      SECTION_DEFINITIONS.map((section) => {
        const values = section.fields.map((f) => (config[f.field].value || '').trim());
        const configuredCount = values.filter(Boolean).length;
        const requiredFields = section.fields.filter((f) => f.required);
        const requiredComplete = requiredFields.filter((f) => (config[f.field].value || '').trim()).length;
        const missingRequiredFields = requiredFields.filter((f) => !(config[f.field].value || '').trim());
        const errorMessages = section.fields
          .map((f) => validation.errors[f.field])
          .filter((m): m is string => Boolean(m));

        return {
          ...section,
          configuredCount,
          requiredCount: requiredFields.length,
          requiredComplete,
          missingRequiredCount: missingRequiredFields.length,
          missingRequiredFields,
          errorMessages,
          hasBlockingIssue: missingRequiredFields.length > 0 || errorMessages.length > 0,
          isComplete: missingRequiredFields.length === 0 && configuredCount > 0,
        };
      }),
    [config, validation.errors]
  );

  const activeSectionState = useMemo(
    () => sectionStates.find((s) => s.key === activeSection) ?? sectionStates[0],
    [activeSection, sectionStates]
  );

  const firstValidationError = useMemo(() => {
    for (const section of sectionStates) {
      const msg = section.errorMessages[0];
      if (msg) return msg;
    }
    return null;
  }, [sectionStates]);

  const blockingReasons = useMemo(() => {
    const reasons: string[] = [];
    if (revisionBlockedReason) reasons.push(revisionBlockedReason);
    if (!validation.isValid) {
      if (Object.keys(validation.errors).length === 1 && firstValidationError) {
        reasons.push(firstValidationError);
      } else if (validation.missingFields.length > 0) {
        reasons.push(`${validation.missingFields.length} required mapping${validation.missingFields.length === 1 ? '' : 's'} missing`);
      } else if (firstValidationError) {
        reasons.push(firstValidationError);
      }
    }
    return reasons;
  }, [firstValidationError, revisionBlockedReason, validation.errors, validation.isValid, validation.missingFields.length]);

  const footerContent = useMemo(() => {
    const totalRequired = sectionStates.reduce((sum, section) => sum + section.requiredCount, 0);
    const completedRequired = sectionStates.reduce((sum, section) => sum + section.requiredComplete, 0);
    return (
      <WorkflowFooter
        isReady={blockingReasons.length === 0 && setupConfirmed}
        readyText={`${completedRequired}/${totalRequired} required mappings selected`}
        blockedReasons={blockingReasons}
      />
    );
  }, [blockingReasons, sectionStates, setupConfirmed]);

  const handleDefaultPropertiesChange = useCallback(
    (checked: boolean) => {
      setDefaultPropertiesConfirmed(checked);
      markSetupValueChanged();
    },
    [markSetupValueChanged, setDefaultPropertiesConfirmed]
  );

  const handleTargetChange = useCallback(
    (target: { destination: 'project' | 'family'; bindingKind: 'type' | 'instance' }) => {
      selectedConnectorTargetRef.current = target;
      setParameterTarget(target);
      markSetupValueChanged();
    },
    [markSetupValueChanged, setParameterTarget]
  );

  const focusFirstInvalidSection = useCallback(() => {
    const invalidFieldKey = Object.keys(validation.errors)[0] as ConnectorFieldKey | undefined;
    if (invalidFieldKey && FIELD_SECTION_LOOKUP[invalidFieldKey]) {
      setActiveSection(FIELD_SECTION_LOOKUP[invalidFieldKey]);
      return;
    }
    if (validation.missingFields.length > 0) {
      const missingField = CONNECTOR_BINDING_FIELDS.find((f) => validation.missingFields.includes(f.label));
      if (missingField && FIELD_SECTION_LOOKUP[missingField.field as ConnectorFieldKey]) {
        setActiveSection(FIELD_SECTION_LOOKUP[missingField.field as ConnectorFieldKey]);
      }
    }
  }, [setActiveSection, validation.errors, validation.missingFields]);

  const activeProjectKeyRef = useRef(activeProjectKey);
  useEffect(() => {
    activeProjectKeyRef.current = activeProjectKey;
  }, [activeProjectKey]);

  const replaceConnectorTarget = useCallback(async (): Promise<ConnectorParameterFailure | null> => {
    // An open revision commits here rather than in `handleContinue`: only this
    // path reports an outcome back to the revision, so only this path may leave
    // it waiting for one.
    beginSetupReconciliation();
    const transaction = await applyConnectorTarget({
      connectorTarget: selectedConnectorTargetRef.current,
    });
    if (transaction.applyResult) {
      recordConnectorLiveEvidenceFrontendResult(transaction.applyResult);
    }

    return describeConnectorTargetFailure(transaction);
  }, [applyConnectorTarget, beginSetupReconciliation]);

  const handleContinue = useCallback(async () => {
    if (!validation.isValid) {
      setShowValidationErrors(true);
      focusFirstInvalidSection();
      return;
    }
    if (!setupConfirmed) return;
    const projectKeyAtStart = activeProjectKey;
    if (!projectKeyAtStart) {
      setParameterFailure(missingProjectIdentityFailure);
      return;
    }
    setParameterFailure(null);
    resetConnectorLiveEvidence();
    const failure = e2eConnectorParameterOutcome
      ? reportStubbedConnectorOutcome(e2eConnectorParameterOutcome)
      : await replaceConnectorTarget();
    if (activeProjectKeyRef.current !== projectKeyAtStart) {
      setParameterFailure(changedProjectIdentityFailure);
      return;
    }
    if (failure) {
      setParameterFailure(failure);
      return;
    }
    setParameterFailure(null);
    onContinue();
  }, [
    activeProjectKey,
    e2eConnectorParameterOutcome,
    focusFirstInvalidSection,
    onContinue,
    replaceConnectorTarget,
    setShowValidationErrors,
    setupConfirmed,
    validation.isValid,
  ]);

  /**
   * Unlocks a revision stuck after a part-finished replacement and runs the
   * step again, which is the only thing that can finish the replacement.
   */
  const handleRecoveryRetry = useCallback(() => {
    retrySetupRevision();
    void handleContinue();
  }, [handleContinue, retrySetupRevision]);

  const handleUndoLastAddition = useCallback(async () => {
    await undoLastConnectorParameterAddition();
  }, [undoLastConnectorParameterAddition]);

  useEffect(() => {
    if (issueNavigationContext?.targetPage !== 'connectors') return;
    const focusedItems = issueNavigationContext.actionParams?.connectorFields || issueNavigationContext.affectedItems;
    const nextFocus = issueNavigationContext.actionParams?.connectorSections
      ? {
          connectorBindings: issueNavigationContext.actionParams.connectorSections.includes('connectorBindings'),
          bomSettings: issueNavigationContext.actionParams.connectorSections.includes('bomSettings'),
          componentsSettings: issueNavigationContext.actionParams.connectorSections.includes('componentsSettings'),
        }
      : resolveConnectorDestinationFocus(focusedItems);
    setActiveSection(getSectionFromFocusState(nextFocus));
  }, [
    issueNavigationContext?.familyId,
    issueNavigationContext?.targetPage,
    issueNavigationContext?.affectedItems,
    issueNavigationContext?.actionParams?.connectorFields,
    issueNavigationContext?.actionParams?.connectorSections,
    setActiveSection,
  ]);

  const sectionSummaryItems = useMemo<SectionSummaryItem[]>(
    () =>
      sectionStates.map((section) => {
        const statusTone: SectionSummaryItem['statusTone'] = section.isComplete
          ? 'ready'
          : section.hasBlockingIssue
            ? 'missing'
            : 'optional';

        return {
        key: section.key,
        title: section.shortTitle,
        detail: section.description,
        help:
          section.key === 'connectors'
            ? connectorBindingTooltips.connectorsSection
            : section.key === 'bom'
              ? connectorBindingTooltips.bomSection
              : connectorBindingTooltips.componentsSection,
        helpLabel: `${section.shortTitle} connector section`,
        statusLabel: section.isComplete
          ? 'Ready'
          : section.hasBlockingIssue
            ? 'Needs attention'
            : `${section.configuredCount}/${section.fields.length} set`,
        statusTone,
        selected: activeSection === section.key,
        onClick: () => setActiveSection(section.key),
        role: 'tab',
        ariaSelected: activeSection === section.key,
        ariaControls: `connector-section-panel-${section.key}`,
        id: `connector-section-tab-${section.key}`,
        tabIndex: activeSection === section.key ? 0 : -1,
      };
    }),
    [activeSection, sectionStates, setActiveSection]
  );

  const activeSectionStatusLabel = activeSectionState.isComplete
    ? 'Ready'
    : activeSectionState.hasBlockingIssue
      ? 'Needs attention'
      : `${activeSectionState.configuredCount}/${activeSectionState.fields.length} set`;
  const activeSectionStatusTone = activeSectionState.isComplete
    ? 'ready'
    : activeSectionState.hasBlockingIssue
      ? 'missing'
      : 'optional';

  return (
    <>
      <OperationProgressWindowRequest
        id="workflow-connector-parameter-addition"
        open={isAddingParameters}
        priority={100}
        suppressInWebView2
        model={parameterProgress ?? {
          title: 'Applying connector parameter setup',
          message: 'Revit is creating missing parameters and repairing incompatible connector bindings.',
          detail: activeConnectorOperation?.description ?? connectorTargetApplication,
          status: 'running',
          percent: null,
          locked: true,
        }}
      />

      <MainLayout
        title="Connector binding"
        maxWidth={960}
        maxHeight="92vh"
        showBack
        onBack={onGoBack}
        showNext
        onNext={handleContinue}
        nextText={connectorOperationLive ? 'Applying…' : hasUndoableConnectorAddition ? continueToNextStepText : 'Apply and continue'}
        nextDisabled={!canProceedWithForm}
        compactWorkflowChrome
        workflowChromeVariant="wizard"
        showRuntimeBadgeInUtilityChrome={false}
        footerContent={footerContent}
      >
        <ValidationNavigationNotice applicableTargetPages={['connectors']} />

        <WorkflowStepShell sx={{ width: '100%', py: 0.75 }}>
          <Box sx={{ display: 'grid', gap: layoutGap.row, minWidth: 0 }}>
            <ParameterSetupScopePanel
              layout="minimal"
              eyebrow=""
              disabled={connectorOperationLive}
              title="Set the connector parameter target"
              description={<Typography component="span" variant="inherit" sx={{ display: 'block', textWrap: 'balance' }}>Select where Revit creates and stores connector parameters and whether their values are defined by TYPE or INSTANCE. Clicking Apply and Continue creates or repairs any missing parameter bindings. The mappings below determine the PCF connection, component, and BOM data.</Typography>}
              statusLabel={setupConfirmed ? 'Set' : 'Missing'}
              statusTone={setupConfirmed ? 'set' : 'missing'}
              blocker={!setupConfirmed ? 'Continue blocked: confirm that Revit may create or repair missing connector parameters' : null}
              target={{
                destination: config.parameterDestination,
                bindingKind: config.parameterBindingKind,
              }}
              recommendedTarget={{ destination: 'project', bindingKind: 'type' }}
              onTargetChange={handleTargetChange}
              confirmed={Boolean(setupConfirmed)}
              autoConfirmed={false}
              onConfirmedChange={handleDefaultPropertiesChange}
              modeLegend="Connector parameter creation mode"
              modeAriaLabel="Connector parameter creation mode"
              setupHelp={connectorBindingTooltips.setupPanel}
              modeHelp={
                <HelpTooltipTrigger
                  label="Connector parameter creation mode"
                  help={connectorBindingTooltips.parameterMode}
                  placement="bottom-start"
                />
              }
              notice={
                config.parameterDestination === 'family'
                ? `Use this option only when parameters must be stored inside editable Revit families. Family ${config.parameterBindingKind === 'type' ? 'TYPE' : 'INSTANCE'} means ${
                    config.parameterBindingKind === 'type'
                      ? 'all instances of the same family type share one value'
                      : 'each placed family instance stores its own value'
                  }. System families, such as pipes, continue to use Project ${config.parameterBindingKind === 'type' ? 'TYPE' : 'INSTANCE'} parameters.`
                : `${selectedTargetLabel} stores values on all selected Revit connector categories.`
              }
              noticeHelp={
                <HelpTooltipTrigger
                  label="Connector parameter target behavior"
                  help={connectorBindingTooltips.pipeException}
                  placement="bottom-start"
                />
              }
              appliedStatus={{
                visible: hasUndoableConnectorAddition,
                message: connectorUndoAffordance.message,
                severity: connectorUndoAffordance.severity,
                onRevert: connectorUndoAffordance.canRevert
                  ? () => void handleUndoLastAddition()
                  : undefined,
                revertHelp: connectorBindingTooltips.revertSetup,
              }}
            />

            {connectorOperationLive && (
              <WorkflowNotice
                severity={connectorSelectionBlockedByMismatch ? 'warning' : 'info'}
                title={activeConnectorOperation.description}
              >
                {connectorSelectionBlockedByMismatch
                  ? 'The selected storage mode is saved. Wait for Revit to finish the current operation, then select Apply and continue again.'
                  : 'Wait for Revit to finish creating or repairing connector parameters. The controls unlock automatically.'}
              </WorkflowNotice>
            )}

            {parameterFailure && (
              <WorkflowNotice
                severity="error"
                title="Connector parameter setup could not be applied"
              >
                <Box>{parameterFailure.summary}</Box>
                {parameterFailure.details.length > 0 && (
                  <Box component="ul" sx={{ my: 0, pl: 3 }}>
                    {parameterFailure.details.map((detail) => (
                      <li key={detail}>{detail}</li>
                    ))}
                  </Box>
                )}
              </WorkflowNotice>
            )}

            {setupRevisionRecovery && (
              <WorkflowNotice severity="error" title={setupRevisionRecovery.title}>
                <Box>{setupRevisionRecovery.detail}</Box>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  sx={{ mt: 0.75 }}
                  onClick={handleRecoveryRetry}
                >
                  {setupRevisionRecovery.actionLabel}
                </Button>
              </WorkflowNotice>
            )}

            <SectionSwitcher
              ariaLabel="Connector output mapping sections"
              items={sectionSummaryItems}
              presentation="tabs"
              action={
                <RevitParameterCatalogAction
                  disabled={!activeProjectKey || connectorOperationLive}
                  isLoading={isLoadingAvailableOptions}
                  onLoad={() => void loadAvailableOptions(true)}
                />
              }
            />

            <EditorSurface sx={{ p: 1, minWidth: 0 }}>
              <Box
                role="tabpanel"
                id={`connector-section-panel-${activeSectionState.key}`}
                aria-labelledby={`connector-section-tab-${activeSectionState.key}`}
                sx={{ display: 'grid', gap: layoutGap.row }}
              >
                <ActiveSectionHeader
                  title={activeSectionState.title}
                  description={activeSectionState.description}
                  statusLabel={activeSectionStatusLabel}
                  statusTone={activeSectionStatusTone}
                />

                {activeSectionState.hasBlockingIssue && (
                  <WorkflowNotice severity="warning">
                    {activeSectionState.missingRequiredCount > 0
                      ? `Select a Revit parameter for ${activeSectionState.missingRequiredCount} required PCF or BOM field${activeSectionState.missingRequiredCount === 1 ? '' : 's'}.`
                      : activeSectionState.errorMessages[0]}
                  </WorkflowNotice>
                )}

                <MappingFieldGrid columns={{ xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }}>
                  {activeSectionState.fields.map((field) => (
                    <CompactFieldRow
                      key={field.field}
                      label={field.label.replace(' *', '')}
                      value={config[field.field].value}
                      onChange={fieldHandlers[field.field]}
                      options={optionGroups[field.optionGroup]}
                      help={field.help}
                      error={getFieldError(field.field)}
                      validationFocused={isValidationFocused(...field.focusTokens)}
                      required={field.required}
                    />
                  ))}
                </MappingFieldGrid>
              </Box>
            </EditorSurface>
          </Box>
        </WorkflowStepShell>
      </MainLayout>
    </>
  );
};

export default ConnectorBindingPage;
